import Box from '@amzn/awsui-components-core/box';
import { AgGridReact } from 'ag-grid-react';
import React from 'react';
import { ReportGridToolbar } from 'src/components/star-grid/components/ReportGridToolbar';
import { ReportHierarchyHeader, ReportPeriodHeader } from 'src/components/star-grid/components/ReportGridHeaders';
import { usePnlReportGrid } from 'src/components/star-grid/hooks/usePnlReportGrid';
import { PnlGridRow, PnlPivotModel } from 'src/components/star-grid/starGridTypes';
import { pnlDefaultColDef } from 'src/components/star-grid/utils/pnlGrid';
import './styles/pnl-report-grid.scss';

export interface PnlReportGridProps {
  readonly reportName?: string;
  readonly compactColumnHeaders?: boolean;
  readonly reportFamily?: string;
  readonly model: PnlPivotModel;
  /** True while a (re-)query is in flight — shows the grid's loading overlay. */
  readonly loading: boolean;
  /** Selected display-scale id (drives client-side value formatting via grid context). */
  readonly scaleId: string;
  /** Dimensions currently in the row hierarchy — used to filter the drill menu. */
  readonly groupBy: readonly string[];
  /** Dimensions the report can drill into (not already grouped). */
  readonly drillableDimensions: readonly string[];
  /** Called when the user picks a drill dimension from the context menu. */
  readonly onDrillDown: (dimension: string) => void;
  /**
   * Per-ROW drill targets and handler, as Prophecy does it: `Drill down by ›` on a row label breaks THAT
   * row down and splices the children beneath it. When absent the menu falls back to
   * `onDrillDown`, which re-groups the whole report.
   */
  readonly rowDrillTargets?: readonly { dimension: string; hierarchy: string; label: string }[];
  readonly onRowDrill?: (row: PnlGridRow, target: { dimension: string; hierarchy: string; label: string }) => void;
  /** Asks for `rowDrillTargets` when a row menu opens before they are loaded. */
  readonly onRowDrillMenu?: () => void;
  /** Whether a row already has children showing, so the menu offers Collapse instead of another drill. */
  readonly isRowExpanded?: (row: PnlGridRow) => boolean;
  readonly onRowCollapse?: (row: PnlGridRow) => void;
  /** `Filter by` — scopes the report to the clicked row's member, the way the deck's own menu does. */
  readonly onFilterByRow?: (row: PnlGridRow) => void;
  /** A click on a row's label cell (by its column field); the report viewer uses it to open an expandable row. */
  readonly onRowLabelClick?: (row: PnlGridRow, field: string) => void;
  /** On a two-dimension report: whether a row's OUTER line has its children showing. */
  readonly isOuterExpanded?: (row: PnlGridRow) => boolean;
  /** Optional field → human label map for the row-hierarchy header + drill menu. */
  readonly dimensionLabels?: Record<string, string>;
  /** Signals that result rows (or the empty state) are ready for a paint. */
  readonly onRowsRendered?: () => void;
  /**
   * Grow to fit every row instead of scrolling inside a fixed viewport.
   *
   * For the Studio's layout preview: the grid there is a bounded SAMPLE, and pinning it to 70vh made
   * the table the only scrollable thing on the page -- the preview's own filter bar and footer sat
   * outside it. Left off for a saved report, where the row count is unbounded and an autoHeight grid
   * would render every row at once.
   */
  readonly autoHeight?: boolean;
}

const GRID_COMPONENTS = { pnlPeriodHeader: ReportPeriodHeader, pnlHierarchyHeader: ReportHierarchyHeader };
const GRID_SIDEBAR = { hiddenByDefault: true, toolPanels: ['columns'] };

const PnlReportGrid: React.FC<PnlReportGridProps> = (props) => {
  const state = usePnlReportGrid(props);
  return (
    <section className="sg-report-table" aria-label={props.reportName}>
      <ReportGridToolbar state={state} />
      {/* Only an EMPTY result says "no rows"; a pending one is still loading. Keyed on rows alone,
          this claimed the filters matched nothing for the whole query, then swapped to the data --
          reading as a failure that fixed itself. While loading, the grid renders and shows its own
          `loading` overlay instead. */}
      {props.model.rows.length === 0 && !props.loading ? (
        <Box padding="l" textAlign="center">
          {state.text('noReportRows')}
        </Box>
      ) : (
        <div className={`sg-pnl-grid-wrapper${props.autoHeight ? ' sg-pnl-grid-autoheight' : ''}`} ref={state.viewportRef}>
          <div className="sg-pnl-grid">
            <AgGridReact<PnlGridRow>
              ref={state.gridRef}
              {...(props.autoHeight ? { domLayout: 'autoHeight' as const } : {})}
              rowData={props.model.rows}
              getRowId={props.model.axisColumns ? state.getRowId : undefined}
              columnDefs={state.columnDefs}
              defaultColDef={pnlDefaultColDef}
              loading={props.loading}
              treeData={!state.statement}
              getDataPath={state.statement ? undefined : state.getDataPath}
              autoGroupColumnDef={state.statement ? undefined : state.autoGroupColumnDef}
              groupDefaultExpanded={1}
              aggFuncs={state.aggFuncs}
              getRowClass={state.getRowClass}
              suppressAggFuncInHeader
              context={{ scaleId: props.scaleId }}
              theme={state.theme}
              sideBar={GRID_SIDEBAR}
              components={GRID_COMPONENTS}
              cellSelection
              // Double-clicking a cell selects its VALUE for copying, as a spreadsheet does. AG Grid
              // blocks native text selection by default; `ensureDomOrder` keeps a multi-cell text
              // selection in on-screen order despite row virtualisation.
              enableCellTextSelection
              ensureDomOrder
              rowBuffer={20}
              rowHeight={state.rowHeight}
              getRowHeight={state.getRowHeight}
              headerHeight={50}
              groupHeaderHeight={29}
              quickFilterText={state.search}
              getContextMenuItems={state.getContextMenuItems}
              onGridSizeChanged={state.resizeLabelColumn}
              onGridReady={state.gridReady}
              onFirstDataRendered={state.firstDataRendered}
              onRowDataUpdated={state.rowDataUpdated}
              onCellClicked={state.labelClicked}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default PnlReportGrid;
